import { format } from 'date-fns'
import { isNumber } from 'lodash'

import constants_work_experience from '../constants/work-experience'
import { CSV_PROFILES } from './csv'
import { getPrimaryTalentStatus } from './spaces-table-data'
import { parseAdditionalData } from './talent-details'
import { getTalentStatusText } from './talent-statuses'
import { TALENT_STORAGE_COLUMN, isStorageLocked } from './talent-storage-table'

/**
 * Reine Helfer fuer die Spalten des Talent-CSV-Exports. Kein React, keine
 * Netzwerkzugriffe, damit die Logik ohne Rendering testbar bleibt.
 *
 * Eine Spalte ist ueberall dasselbe Objekt:
 * { id, label, group, accessor(space, ctx), required?, defaultEnabled?, coverage?, origin? }
 *
 * Statische Spalten behalten ihre bisherigen Ids, damit die Kopfzeile
 * bestehender Exporte unveraendert bleibt.
 *
 * Dynamische Spalten werden ueber die Feld-UUID identifiziert, nicht ueber das
 * Label. Das Label ist im Datenmodell ein Snapshot pro Space, es ist nirgends
 * auf Eindeutigkeit geprueft, darf leer sein und wird bei Datei-Feldern durch
 * sanitizeFileName um Umlaute gekuerzt. Eine Gruppierung darueber wuerde ein
 * umbenanntes Feld in zwei halbleere Spalten zerlegen und bei zwei gleich
 * benannten Feldern derselben Stelle still einen Wert verschlucken.
 *
 * Performance: parseAdditionalData laeuft genau einmal pro Space. Das Ergebnis
 * liegt im ctx.index, die Accessoren machen daraus nur noch einen Map-Lookup.
 * Pro Zelle zu parsen kostet bei 5000 Spaces mal 50 Spalten rund das Dreizehn-
 * fache, deshalb steckt der Index in der Signatur und nicht in einer Konvention.
 */

const I18N = 'spaces.actions.csv_export.'

export const EXPORT_GROUPS = [
  { id: 'identity', labelKey: I18N + 'group_identity' },
  { id: 'contact', labelKey: I18N + 'group_contact' },
  { id: 'process', labelKey: I18N + 'group_process' },
  { id: 'additional', labelKey: I18N + 'group_additional' }
]

// Eine Datei mit mehr Spalten ist in Excel nicht mehr zu gebrauchen, und die
// gemerkte Auswahl liegt im customer_dashboard_meta, das bei jedem Kollegen
// auf der Nutzerverwaltung mitgeladen wird.
export const EXPORT_COLUMN_LIMIT = 60

// Die Spalte Talentspeicher gibt es nur mit gesperrten Talenten. Ohne sie
// fällt die Id in resolveSelectedIds weg, der Werkszustand bleibt gleich.
export const DEFAULT_SELECTED_COLUMN_IDS = [
  'identification',
  'name',
  TALENT_STORAGE_COLUMN
]

export const CUSTOM_PREFIX = 'custom:'
export const FILE_PREFIX = 'file:'
export const WORK_EXPERIENCE_COLUMN_ID = CUSTOM_PREFIX + 'work_experience'

const SYNTHETIC_ASSESSMENT_IDS = ['TALENTPOOL', 'ATTRACT']

// Gesperrte Talente (Talentspeicher) kommen maskiert aus useTalentsQuery.
// Score und Zusatzangaben bekommen in der Datei „gesperrt" statt einer leeren
// Zelle, sonst sähe es aus, als hätte das Talent nichts angegeben.
const LOCKED_I18N = 'pricing.talent_locked.'
const lockedValue = (ctx) => ctx.t(LOCKED_I18N + 'csv_value')

const formatDate = (value, profile) => {
  if (!value) return null
  if (profile !== CSV_PROFILES.EXCEL) return value

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return format(date, 'dd.MM.yyyy HH:mm')
}

const STATIC_COLUMNS = [
  {
    id: 'identification',
    group: 'identity',
    labelKey: I18N + 'label_identification',
    required: true,
    accessor: (space) => space.identification ?? null
  },
  {
    id: 'name',
    group: 'identity',
    labelKey: I18N + 'label_name',
    defaultEnabled: true,
    accessor: (space) => space.name ?? null
  },
  {
    id: 'firstname',
    group: 'identity',
    labelKey: I18N + 'label_firstname',
    accessor: (space) => space.firstname ?? space.person?.firstname ?? null
  },
  {
    id: 'lastname',
    group: 'identity',
    labelKey: I18N + 'label_lastname',
    accessor: (space) => space.lastname ?? space.person?.lastname ?? null
  },
  {
    id: 'space_id',
    group: 'identity',
    labelKey: I18N + 'label_space_id',
    accessor: (space) => space.id ?? null
  },
  {
    id: 'talent_link',
    group: 'identity',
    labelKey: I18N + 'label_talent_link',
    accessor: (space, ctx) =>
      ctx.origin ? `${ctx.origin}/talent/${space.id}` : null
  },
  {
    id: 'email',
    group: 'contact',
    labelKey: I18N + 'label_email',
    accessor: (space) =>
      space.email ??
      space.person?.email ??
      space.person?.invitation_email ??
      null
  },
  {
    id: 'phone',
    group: 'contact',
    labelKey: I18N + 'label_phone',
    accessor: (space) => space.phone ?? space.person?.phone ?? null
  },
  {
    id: 'postcode',
    group: 'contact',
    labelKey: I18N + 'label_postcode',
    accessor: (space) => space.person?.postcode ?? null
  },
  {
    id: 'city',
    group: 'contact',
    labelKey: I18N + 'label_city',
    accessor: (space) => space.person?.city ?? null
  },
  {
    id: 'source',
    group: 'process',
    labelKey: I18N + 'label_source',
    accessor: (space, ctx) =>
      ctx.careersMap?.get(space.assessment_id)?.title ?? null
  },
  {
    id: 'status',
    group: 'process',
    labelKey: I18N + 'label_status',
    accessor: (space, ctx) => {
      const { status } = getPrimaryTalentStatus(space)

      return (
        getTalentStatusText({
          status,
          t: ctx.t,
          language: ctx.language,
          progress: space.progress
        }) || null
      )
    }
  },
  {
    id: 'progress',
    group: 'process',
    labelKey: I18N + 'label_progress',
    accessor: (space) => (isNumber(space.progress) ? space.progress : null)
  },
  {
    // Nicht space.score lesen, getScore liefert dort bei fehlender Bewertung
    // den Anzeigestring '-', der in einer Datei nichts zu suchen hat.
    id: 'score',
    group: 'process',
    labelKey: I18N + 'label_score',
    accessor: (space, ctx) => {
      if (isStorageLocked(space)) return lockedValue(ctx)

      return isNumber(space.matching_score_to_space_career)
        ? Math.round(space.matching_score_to_space_career)
        : null
    }
  },
  {
    id: 'via',
    group: 'process',
    labelKey: I18N + 'label_via',
    accessor: (space, ctx) => {
      if (space.via === 'EMAIL') return ctx.t(I18N + 'via_email')
      if (space.via === 'LINK') return ctx.t(I18N + 'via_link')

      return null
    }
  },
  {
    id: 'updatedAt',
    group: 'process',
    labelKey: I18N + 'label_updated_at',
    accessor: (space, ctx) => formatDate(space.updatedAt, ctx.profile)
  },
  {
    id: 'createdAt',
    group: 'process',
    labelKey: I18N + 'label_created_at',
    accessor: (space, ctx) => formatDate(space.createdAt, ctx.profile)
  }
]

export const parseFieldDefinitions = (raw) => {
  try {
    const parsed = JSON.parse(raw || '[]')

    return Array.isArray(parsed) ? parsed : []
  } catch (err) {
    return []
  }
}

// Fuer Talentpool und Attract steht in assessment_id ein Literal, die echte
// career_id ueberlebt den Mapper aber im Spread und traegt die Definitionen.
const definitionSourceId = ({ assessment_id, career_id }) =>
  assessment_id && !SYNTHETIC_ASSESSMENT_IDS.includes(assessment_id)
    ? assessment_id
    : career_id

/**
 * Kopfzeilen eindeutig machen. Erst den Stellentitel anhaengen, wo es einen
 * gibt, danach einen Zaehler. Zwei gleichnamige Felder derselben Stelle sind
 * erlaubt, deshalb reicht der Titel allein nicht.
 */
export const dedupeHeaders = (columns) => {
  const totals = new Map()
  columns.forEach(({ label }) =>
    totals.set(label, (totals.get(label) || 0) + 1)
  )

  const withOrigin = columns.map((column) =>
    totals.get(column.label) > 1 && column.origin
      ? { ...column, label: `${column.label} (${column.origin})` }
      : column
  )

  const seen = new Map()

  return withOrigin.map((column) => {
    const count = (seen.get(column.label) || 0) + 1
    seen.set(column.label, count)

    return count === 1
      ? column
      : { ...column, label: `${column.label} ${count}` }
  })
}

const shortId = (id) => String(id).slice(0, 8)

// parseAdditionalData faellt bei leerem Label auf prettifyKey(id) zurueck, aus
// der UUID wird also ein Titel wie "5660Be17 35C0 ...". Solche Pseudo-Labels
// duerfen nicht als Kopfzeile durchgehen.
const looksLikeId = (label, id) =>
  String(label).toLowerCase().replace(/\s+/g, '') === String(id).toLowerCase()

const displayLabel = (label, id) => {
  const text = typeof label === 'string' ? label.trim() : ''

  return !text || looksLikeId(text, id) ? '' : text
}

/**
 * @param {Object}   input
 * @param {Array}    input.spaces        gemappte Spaces aus getSpacesTableData
 * @param {Map}      input.careersMap    careers_map aus talents.js
 * @param {Array}    input.partnerFields geparste Partner-Definitionen
 * @param {Function} input.t
 * @param {string}   input.language
 * @param {string}   [input.origin]      window.location.origin
 * @param {string}   [input.partnerOriginLabel]
 * @returns {{ columns: Array, ctx: Object }}
 */
export const buildExportColumns = ({
  spaces = [],
  careersMap = new Map(),
  partnerFields = [],
  t,
  language,
  origin,
  partnerOriginLabel
}) => {
  // 1. Definitionen, geschnitten mit den in der Auswahl vorkommenden Stellen.
  //    Ohne den Schnitt saehe ein Kunde mit 40 Stellen immer alle Felder.
  const catalogue = new Map()
  const seenSources = new Set()
  let needsPartnerFields = false

  // Eine Definition gewinnt immer, die Daten fuellen nur Luecken.
  const register = ({ id, label, type }, originTitle, fromDefinition) => {
    if (!id) return

    const existing = catalogue.get(id)
    if (existing && (existing.fromDefinition || !fromDefinition)) return

    catalogue.set(id, {
      id,
      label: displayLabel(label, id),
      type: type === 'file' || type === 'dropdown' ? type : 'text',
      origin: originTitle,
      fromDefinition
    })
  }

  spaces.forEach((space) => {
    const sourceId = definitionSourceId(space)
    const career = sourceId ? careersMap.get(sourceId) : undefined

    if (!career?.app_settings) {
      needsPartnerFields = true
      return
    }

    if (seenSources.has(sourceId)) return
    seenSources.add(sourceId)

    parseFieldDefinitions(
      career.app_settings.application_input_additional_fields
    ).forEach((field) => register(field, career.title, true))
  })

  if (needsPartnerFields) {
    partnerFields.forEach((field) => register(field, partnerOriginLabel, true))
  }

  // 2. Ein Lauf ueber die Spaces baut den Index und ergaenzt Felder, deren
  //    Definition geloescht wurde oder deren Stelle nicht ACTIVE ist.
  const index = new Map()
  const coverage = new Map()
  const snapshots = new Map()
  let workExperienceCoverage = 0

  const countHit = (id) => coverage.set(id, (coverage.get(id) || 0) + 1)

  const rememberSnapshot = (space, field) => {
    const label = displayLabel(field.label, field.id)
    if (!label) return

    const at = Date.parse(space.updatedAt) || 0
    const known = snapshots.get(field.id)
    if (known && known.at >= at) return

    snapshots.set(field.id, { label, at })
  }

  spaces.forEach((space) => {
    const { workExperience, fields, fileFields } = parseAdditionalData(space)

    const values = new Map()
    const fileCounts = new Map()

    fields.forEach((field) => {
      values.set(field.id, field.value)
      countHit(field.id)
      rememberSnapshot(space, field)
      register(field, undefined, false)
    })

    fileFields.forEach((field) => {
      const count = Array.isArray(field.raw?.input) ? field.raw.input.length : 0
      if (count > 0) {
        fileCounts.set(field.id, count)
        countHit(field.id)
      }
      rememberSnapshot(space, field)
      register(
        { id: field.id, label: field.label, type: 'file' },
        undefined,
        false
      )
    })

    if (isNumber(workExperience)) workExperienceCoverage += 1

    index.set(space.id, { values, fileCounts, workExperience })
  })

  // 3. Spalten bauen.
  const columns = STATIC_COLUMNS.map((column) => ({
    ...column,
    label: t(column.labelKey)
  }))

  // Talentspeicher nur, wenn die Liste Sperren trägt. Ohne Sperren (legacy,
  // preview, unter dem Limit) fehlt storage_locked an jeder Zeile.
  if (spaces.some((space) => typeof space?.storage_locked === 'boolean')) {
    const scoreIndex = columns.findIndex(({ id }) => id === 'score')

    columns.splice(scoreIndex + 1, 0, {
      id: TALENT_STORAGE_COLUMN,
      group: 'process',
      label: t(LOCKED_I18N + 'csv_column'),
      defaultEnabled: true,
      accessor: (space, ctx) =>
        isStorageLocked(space)
          ? lockedValue(ctx)
          : ctx.t(LOCKED_I18N + 'csv_in_storage')
    })
  }

  if (workExperienceCoverage > 0) {
    columns.push({
      id: WORK_EXPERIENCE_COLUMN_ID,
      group: 'additional',
      label: t(I18N + 'label_work_experience'),
      coverage: workExperienceCoverage,
      accessor: (space, ctx) => {
        if (isStorageLocked(space)) return lockedValue(ctx)

        const value = ctx.index.get(space.id)?.workExperience
        if (!isNumber(value)) return null

        const labels = constants_work_experience[value]?.label
        if (!labels) return null

        return labels[ctx.language] || labels.de
      }
    })
  }

  catalogue.forEach((entry) => {
    const { id, type } = entry
    const label =
      entry.label ||
      snapshots.get(id)?.label ||
      t(I18N + 'unnamed_field', { short_id: shortId(id) })

    if (type === 'file') {
      columns.push({
        id: FILE_PREFIX + id,
        group: 'additional',
        label: t(I18N + 'file_column_label', { label }),
        origin: entry.origin,
        coverage: coverage.get(id) || 0,
        accessor: (space, ctx) =>
          isStorageLocked(space)
            ? lockedValue(ctx)
            : (ctx.index.get(space.id)?.fileCounts.get(id) ?? null)
      })

      return
    }

    columns.push({
      id: CUSTOM_PREFIX + id,
      group: 'additional',
      label,
      origin: entry.origin,
      coverage: coverage.get(id) || 0,
      accessor: (space, ctx) =>
        isStorageLocked(space)
          ? lockedValue(ctx)
          : (ctx.index.get(space.id)?.values.get(id) ?? null)
    })
  })

  return {
    columns: dedupeHeaders(columns),
    ctx: { t, language, careersMap, origin, index }
  }
}

/**
 * @returns {Array<Array<string|number|null>>}
 */
export const buildExportRows = ({
  spaces = [],
  columns = [],
  ctx,
  profile
}) => {
  const rowCtx = { ...ctx, profile }

  return spaces.map((space) =>
    columns.map((column) => column.accessor(space, rowCtx))
  )
}
